import { FunctionComponent, useState, useCallback, useEffect, useRef, cloneElement } from 'react';
import type { IPaneviewPanelProps } from 'dockview-react';
import { SINGLE_FILTER_HEADER_HEIGHT, SINGLE_FILTER_MIN_HEIGHT, AUTO_COLLAPSE_THRESHOLD } from '../../FiltersPanel.constant';
import { IFilterPanelBodyParams } from './FilterPanelBody.interface';

export const FilterPanelBody: FunctionComponent<IPaneviewPanelProps<IFilterPanelBodyParams>> = (props) => {
  const { api, params } = props;
  const { filterBlock, onCollapseChange, isProgrammaticResizeRef: programmaticResizeRef } = params;
  const [isCollapsed, setCollapsed] = useState(false);
  const prevSizeRef = useRef(0);
  // A pane mounted into a container that is still laying out reports transient near-zero
  // heights; auto-collapse must only react to shrinking from a real expanded height.
  const hasExpandedRef = useRef(false);

  const toggleCollapsed = useCallback(() => {
    setCollapsed((prev) => !prev);
  }, []);

  // Auto-collapse when user resizes panel close to header height
  useEffect(() => {
    const disposable = api.onDidDimensionsChange((event) => {
      if (!isCollapsed) {
        if (event.height <= AUTO_COLLAPSE_THRESHOLD && !programmaticResizeRef?.current && hasExpandedRef.current) {
          setCollapsed(true);
        } else if (event.height > AUTO_COLLAPSE_THRESHOLD) {
          hasExpandedRef.current = true;
          prevSizeRef.current = event.height;
        }
      }
    });

    return () => disposable.dispose();
  }, [api, isCollapsed, programmaticResizeRef]);

  useEffect(() => {
    // Set flag BEFORE any setSize calls to prevent neighboring panels from auto-collapsing
    if (programmaticResizeRef) {
      programmaticResizeRef.current = true;
    }

    if (isCollapsed) {
      if (api.height > AUTO_COLLAPSE_THRESHOLD) {
        prevSizeRef.current = api.height;
      }
      api.setConstraints({
        maximumSize: SINGLE_FILTER_HEADER_HEIGHT,
        minimumSize: SINGLE_FILTER_HEADER_HEIGHT,
      });
      api.setSize({ size: SINGLE_FILTER_HEADER_HEIGHT });
    } else {
      api.setConstraints({
        maximumSize: Number.MAX_SAFE_INTEGER,
        minimumSize: SINGLE_FILTER_HEADER_HEIGHT,
      });
      const restoreHeight = Math.max(prevSizeRef.current, SINGLE_FILTER_MIN_HEIGHT);
      api.setSize({ size: restoreHeight });
    }
    onCollapseChange(api.id, isCollapsed);
  }, [api, isCollapsed, onCollapseChange, programmaticResizeRef]);

  return (
    <div data-panel-collapsed={isCollapsed} style={{ height: '100%' }}>
      {cloneElement(filterBlock, {
        isCollapsed,
        toggleCollapsed,
        hasSiblings: true,
      })}
    </div>
  );
};
